import React, { useState } from "react";

import moment from "moment/moment";

import {
  useDeleteCropMixComposerSettings,
  useGetCropMixComposerSettings,
  useLocation,
  useMutateCropMixComposerSettings,
  useNavigate,
  useTranslation,
} from "utils";

import { Button, Flex, HeaderItemSwitcher, Icon, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { CropMixRowActions } from "pages/app/crop-mix-composer/components/CropMixRowActions";
import { MixNameModal } from "pages/app/crop-mix-composer/components/MixNameModal";
import {
  DRAFT_MIX_ID,
  buildRecommendationSettings,
  getMostRecentlyUpdatedMix,
  getNextAvailableMixName,
} from "pages/app/crop-mix-composer/utils/cropMixComposer";
import { useCreateDraftMix } from "pages/app/crop-mix-composer/utils/useCreateDraftMix";

export const CropMixComposerHeader = ({
  basePath,
  isDrawer,
  activeCropMixId,
  isCommercialCropMix,
  isPageLoading,
  isDraft,
}: {
  basePath: string;
  isDrawer?: boolean;
  activeCropMixId?: string;
  isCommercialCropMix?: boolean;
  isPageLoading?: boolean;
  isDraft?: boolean;
}) => {
  const { t } = useTranslation("cropMixComposer");
  const location = useLocation();
  const navigate = useNavigate();
  const { data: settingsCMC, isPending: isPendingSettingsCMC } = useGetCropMixComposerSettings();
  const { mutateAsync: mutateAsyncCropMixComposerSettings } = useMutateCropMixComposerSettings();
  const { mutateAsync: deleteCropMixComposerSettings } = useDeleteCropMixComposerSettings();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const { setIsDraftMix, setIsNewCropMix, resetSettings } = useCropMixComposerContext();
  const { createDraftMix } = useCreateDraftMix();

  const cropMixes = settingsCMC || [];

  const enterDraft = () => {
    resetSettings();
    setIsDraftMix(true);
    navigate(`${basePath}cmc${location.search}`);
  };

  const draftMixName = getNextAvailableMixName(t("cropMixSelectionModal.myMix"), settingsCMC);

  const handleCreateMix = async (name: string) => {
    const mixToInheritSoilSettingsFrom = getMostRecentlyUpdatedMix(cropMixes);

    const newMix = await mutateAsyncCropMixComposerSettings({
      data: { name, recommendation: { settings: buildRecommendationSettings(mixToInheritSoilSettingsFrom?.recommendation?.settings ?? {}) } },
    });

    resetSettings();
    setIsDraftMix(false);
    setIsNewCropMix(true);
    setShowCreateModal(false);

    if (newMix) navigate(`${basePath}cmc/${newMix.id}/${CMC_ROUTES.SETTINGS}${location.search}`);
  };

  const handleRenameDraft = async (name: string) => {
    const newMix = await createDraftMix(name);
    if (newMix) navigate(`${basePath}cmc/${newMix.id}/${CMC_ROUTES.SETTINGS}${location.search}`);
  };

  const handleDiscardDraft = () => {
    setIsDraftMix(false);
    resetSettings();

    const nextMix = getMostRecentlyUpdatedMix(cropMixes);
    if (nextMix) navigate(`${basePath}cmc/${nextMix.id}${location.search}`);
  };

  const mixNames = cropMixes.map(({ name }) => name);

  const draftItem = {
    id: DRAFT_MIX_ID,
    label: draftMixName,
    subLabel: moment().format("DD.MM.YYYY"),
    actionIcon: (
      <CropMixRowActions
        mixId={DRAFT_MIX_ID}
        mixName={draftMixName}
        isActive
        canDelete={cropMixes.length > 0}
        takenNames={mixNames}
        onRename={handleRenameDraft}
        onDelete={async () => handleDiscardDraft()}
      />
    ),
  };

  const handleMixSelection = (mixId: string) => {
    if (mixId === DRAFT_MIX_ID) return;

    navigate(`${basePath}cmc/${mixId}${location.search}`);
  };

  const handleRenameMix = async (mixId: string, name: string) => {
    await mutateAsyncCropMixComposerSettings({ data: { name }, settingId: mixId });
  };

  const handleDeleteMix = async (mixId: string) => {
    await deleteCropMixComposerSettings({ cropMixId: mixId });

    if (mixId !== activeCropMixId) return;

    const nextMix = getMostRecentlyUpdatedMix(cropMixes.filter((mix) => mix.id !== mixId));
    if (nextMix) {
      navigate(`${basePath}cmc/${nextMix.id}${location.search}`);
      return;
    }

    enterDraft();
  };

  const BackToCustomMixButton = () => (
    <Button
      size="small"
      type="secondary"
      style={{ width: "fit-content" }}
      onClick={() => navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.OVERVIEW}${location.search}`)}
    >
      {t("backToCustomMix")}
    </Button>
  );

  return (
    <Flex className="crop-mix-composer-drawer-header" align="center" justify={isCommercialCropMix ? "space-between" : "flex-start"} gap={"0.5rem"} flex={1}>
      <Flex align="center" gap={"0.5rem"}>
        {isDrawer ? (
          <>
            <Icon iconName="klim/plant-ai" size="m" />
            <Typography variant="h3">{t("drawerTitle")}</Typography>
          </>
        ) : null}
        {isDrawer ? null : (
          <HeaderItemSwitcher
            dataTestId="crop-mix-selection"
            items={[
              ...(isDraft ? [draftItem] : []),
              ...cropMixes.map((mix) => ({
                id: mix.id,
                label: mix.name,
                subLabel: moment(mix.lastUpdated).format("DD.MM.YYYY"),
                actionIcon: (
                  <CropMixRowActions
                    mixId={mix.id}
                    mixName={mix.name}
                    isActive={mix.id === activeCropMixId}
                    canDelete
                    takenNames={mixNames.filter((name) => name !== mix.name)}
                    onRename={(name) => handleRenameMix(mix.id, name)}
                    onDelete={() => handleDeleteMix(mix.id)}
                  />
                ),
              })),
            ]}
            activeItemId={isDraft ? DRAFT_MIX_ID : activeCropMixId}
            isLoading={isPendingSettingsCMC || isPageLoading}
            onSelect={handleMixSelection}
            footerLabel={t("createNew")}
            onFooterClick={() => setShowCreateModal(true)}
          />
        )}
        <MixNameModal
          showModal={showCreateModal}
          formName="createCropMix"
          dataTestId="crop-mix-create-modal"
          title={t("createMixModal.title")}
          okText={t("createMixModal.create")}
          submitErrorMessage={t("errors.createMixFailed")}
          mixName={draftMixName}
          takenNames={mixNames}
          onSubmit={handleCreateMix}
          onCancel={() => setShowCreateModal(false)}
        />
      </Flex>
      {isCommercialCropMix ? <BackToCustomMixButton /> : null}
    </Flex>
  );
};
